import Head from 'next/head';
import React, { useState, useEffect, useMemo } from 'react';
import Router, { useRouter } from 'next/router';
import Breadcrumb from 'components/Breadcrumb';
import Collapsible from 'components/CourseDescription/Collapsible';
import CourseData from 'components/CourseDescription/CourseData';
import ModalCheckout from 'components/CourseDescription/ModalCheckout';
import { Spinner } from 'react-bootstrap';
import { useImageHandler } from 'utils/custom_hooks';
import { readingTimeGenerator, dateGenerator, typeGenerator } from 'utils/product_description';
import { synchronizedSingleCartData } from 'utils/manipulate_array';
import { generateCoverImageUrl } from 'utils/generate_cover_image_url';
import Card from 'components/Landing/Card';
import { getBundleDetails, getBundleRecommendation } from '../../services/bundles';
import BundleTestimonialSection from '../../components/CourseDescription/Section/BundleTestimonialSection';
import ICONS from '../../utils/icons';
import Accordion from '../../components/Accordion';
import BundleTypeBadge from '../../components/bundles/BundleTypeBadge';
import PreviewDocument from '../../components/PreviewDocument';
import { truncateText } from 'utils/helper';
import Button from 'components/Button';
import { BUTTON_TYPE } from 'constants/button';
import { Heading, SubHeading, TextBody } from 'components/Typography/General';
import { FONT_TYPES } from 'variables/font_groups';
import { isMobile } from 'react-device-detect';
import { DETAIL_TITLE } from 'constants/courseDescription';
import { trackAddToCart, trackCtaClick, trackRemoveFromCart } from 'lib/analytics';

export async function getServerSideProps(context) {
  const { req, query } = context;
  const { bundleId } = query;

  const bundleDetail = await getBundleDetails(bundleId);
  const baseUrl = `${req.protocol || 'https'}://${req.headers.host}`;

  if (!bundleDetail) {
    return {
      notFound: true,
    };
  }

  const isBaseDomain = baseUrl === process.env.NEXT_PUBLIC_BASE_PATH;
  const _id = bundleDetail._id;
  const bundleRecommendation = await getBundleRecommendation({
    notIn: _id,
    provider: isBaseDomain ? null : `&provider=${bundleDetail.provider.code}`,
  });

  return {
    props: {
      bundleDetail,
      bundleRecommendation,
    },
  };
}

const BundleDescription = (props) => {
  const { bundleDetail, bundleRecommendation } = props;

  const {
    center,
    description,
    discount,
    price,
    duration,
    image,
    language,
    productId,
    provider,
    features,
    outcome,
    outline,
    prerequisite,
    audience,
    purchaseOption,
    referenceNumber,
    releasedAt,
    title,
    totalPages,
    type,
    isHasCertification,
    version,
    testimonials,
    slug,
    faqs,
    facilitator,
    items,
    bundleType,
    files,
    status,
    lmsBundleId,
    plannedCoursesCount,
    plannedCoursesProductsCount,
    plannedProductsCount,
    newCoursesProductsReleasedMonthly,
    newCoursesReleasedMonthly,
    newProductsReleasedMonthly,
  } = bundleDetail;

  const router = useRouter();
  const memoizedOptionList = useMemo(() => [provider?.path], [provider?.path]);
  const { currentImage, handleIsImageAvailable, handleImageToDefault } = useImageHandler({
    optionList: memoizedOptionList,
    data: bundleDetail,
  });

  console.log('bundleDetail', bundleDetail);

  const timeInvested = readingTimeGenerator(Array.isArray(duration) && duration[0] ? duration[0] : null);
  const timeEstimation = readingTimeGenerator(Array.isArray(duration) && duration[1] ? duration[1] : null);
  const productType = typeGenerator(type);
  const bundleReleaseDate = dateGenerator(releasedAt);

  const [isLoading, setIsLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [currentSelect, SetCurrentSelect] = useState('');
  const [recommendedExpand, setRecommendedExpand] = useState(false);
  const [openModalCheckout, setOpenModalCheckout] = useState(false);
  const [faqItem, setFaqItem] = useState([]);
  const [openFaqItem, setOpenFaqItem] = useState(false);

  const isAddedToCart = cart.filter((data_cart) => data_cart.itemId === lmsBundleId).length !== 0;

  const handleAddToCart = (data) => {
    const newCart = synchronizedSingleCartData({ item: data, isProduct: false });

    setIsLoading(true);
    let list_cart = [...cart];
    let new_data = [];
    const price = parseFloat(data?.price?.$numberDecimal ? data?.price?.$numberDecimal : data?.price || 0);

    if (list_cart.filter((cart) => cart.itemId == newCart.itemId).length !== 0) {
      new_data = list_cart.filter((cart) => cart.itemId !== newCart.itemId);
      trackCtaClick({
        page_type: 'bundle_detail',
        cta_label: 'remove_from_cart',
        location: 'bundle_course_detail',
        course_id: data.courseId || data.productId,
      });

      trackRemoveFromCart({
        page_type: 'bundle_detail',
        currency: 'USD',
        value: (price) * -1,
        items: [
          {
            item_id: data.courseId || data.productId,
            item_name: data.name,
            item_category: data.categoryName || 'unknown',
            item_variant: 'bundle',
            price: price,
            quantity: 0,
          },
        ],
        course_id: data.courseId || data.productId,
      });
      Router.reload(window.location.pathname);
    } else {
      trackCtaClick({
        page_type: 'bundle_detail',
        cta_label: 'add_to_cart',
        location: 'bundle_course_detail',
        course_id: data.courseId || data.productId,
      });

      trackAddToCart({
        page_type: 'bundle_detail',
        currency: 'USD',
        value: price,
        items: [
          {
            item_id: data.courseId || data.productId,
            item_name: data.name,
            item_category: data.categoryName || 'unknown',
            item_variant: 'bundle',
            price: price,
            quantity: 1,
          },
        ],
        course_id: data.courseId || data.productId,
      });
      list_cart.forEach((list) => {
        new_data.push(list);
      });
      new_data.push(newCart);
      setOpenModalCheckout(true);
    }

    const synchronizedCartData = new_data.map((item) => {
      return synchronizedSingleCartData({ item: item, isProduct: item.isProduct || false });
    });

    setCart(synchronizedCartData);
    localStorage.setItem('cart', JSON.stringify(synchronizedCartData));
  };

  const handleCollaps = async (currentSelected) => {
    if (currentSelect == currentSelected) {
      currentSelected = '';
    }
    SetCurrentSelect(currentSelected);
  };

  useEffect(() => {
    let cart = [];
    if (localStorage.getItem('cart')) {
      cart = JSON.parse(localStorage.getItem('cart'));
    }

    const newCart = cart.map((cartItem) => {
      return synchronizedSingleCartData({ item: cartItem, isProduct: cartItem.isProduct || false });
    });

    setCart(newCart);

    if (faqs.length > 0) {
      setFaqItem(faqs);
    }
  }, []);

  const getTotalCoursesInBundles = () => {
    // if (bundleType === 1) {
    //   /**
    //    * For bundle type product, we do not display courses in bundle information
    //    */
    //   return null;
    // }
    return items.length;
    // return items.filter((item) => item.type === 0).length;
  };

  return (
    <div className="bundle-container">
      <Head>
        <title>{title}</title>
      </Head>

      {isLoading ? (
        <div className="loading">
          <Spinner animation="border" variant="white" style={{ width: '50px', height: '50px' }} />
        </div>
      ) : (
        ''
      )}

      <div className="course-description-breadcrumb">
        <Breadcrumb
          data={[
            { title: 'Home', url: '/' },
            { title: 'All bundles', url: '/category?bundles=all' },
            { title: truncateText(title), active: true },
          ]}
          variant="no-padding"
        />
      </div>
      <div className="image-container mobile w-100 d-lg-none d-block ">
        <img src={generateCoverImageUrl(image.path)} alt="course image" className="image w-100 h-100"></img>
      </div>

      <div className="course-description">
        <div className="course-description__overview">
          <div className="course-name-avatar">
            <Heading variety={2} className="d-lg-none d-block title-1 mb-3">
              {title}
            </Heading>
            {handleIsImageAvailable(provider?.logo) ? (
              <div className="user-image">
                <div className="user-image-container">
                  <img src={currentImage} onError={() => handleImageToDefault()} />
                </div>
              </div>
            ) : (
              <div className="user-image">
                <p className="title-2">{provider?.name}</p>
                <div className="user-image-container">
                  <img src={currentImage} onError={() => handleImageToDefault()} />
                </div>
              </div>
            )}
          </div>
          <div className="d-flex">
            <Heading variety={2} className="d-none d-lg-block title-1" style={{width: '75%'}}>
              {title}
            </Heading>
            <div className="badge-container">
              <BundleTypeBadge bundleType={bundleType} />
            </div>
          </div>
          {Array.isArray(files) && files.length > 0 && (
            <div className={'mobile-preview-button'}>
              <PreviewDocument buttonLabel={'Preview'} documentLinks={files.map((file) => file.path)} />
            </div>
          )}
          <div className="d-lg-none d-block border-bottom">
            <CourseData
              isBundle={true}
              discount={discount}
              courseOrganizer={provider?.name}
              price={price}
              plannedCoursesCount={plannedCoursesCount}
              plannedCoursesProductsCount={plannedCoursesProductsCount}
              plannedProductsCount={plannedProductsCount}
              newCoursesProductsReleasedMonthly={newCoursesProductsReleasedMonthly}
              newCoursesReleasedMonthly={newCoursesReleasedMonthly}
              newProductsReleasedMonthly={newProductsReleasedMonthly}
              bundleType={bundleType}
              productType={productType}
              totalPages={totalPages}
              courseDuration={timeInvested}
              timeEstimation={timeEstimation}
              language={language || ''}
              center={center}
              publishDate={bundleReleaseDate}
              referenceNumber={referenceNumber}
              certificateAwarded={isHasCertification}
              version={version}
              totalCourseBundling={getTotalCoursesInBundles()}
              status={status ? 'Published' : null}
              isMobile
            />
          </div>
          <SubHeading variety={isMobile ? 2 : 3} className="title-2 mt-4 mt-lg-0" id="about-title">
            About the Bundle
          </SubHeading>
          <TextBody
            variety={isMobile ? 2 : 4}
            id="about-description"
            className="descriptions"
            dangerouslySetInnerHTML={{ __html: description }}
          ></TextBody>

          {features && features.length > 0 ? (
            <Collapsible
              title={DETAIL_TITLE.KEY_FEATURES}
              description={features}
              isOpen={DETAIL_TITLE.KEY_FEATURES === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {provider.information && provider.information.length > 0 ? (
            <Collapsible
              title={DETAIL_TITLE.PRODUCT_PROVIDER_INFORMATION}
              description={provider.information}
              isOpen={DETAIL_TITLE.PRODUCT_PROVIDER_INFORMATION === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {purchaseOption && purchaseOption.length > 0 ? (
            <Collapsible
              title={DETAIL_TITLE.OTHER_RELATED_COURSES}
              description={purchaseOption}
              isOpen={DETAIL_TITLE.OTHER_RELATED_COURSES === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {audience && audience.length > 0 ? (
            <Collapsible
              title={DETAIL_TITLE.AUDIENCE}
              description={audience}
              isOpen={DETAIL_TITLE.AUDIENCE === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {prerequisite && prerequisite.length > 0 ? (
            <Collapsible
              title={DETAIL_TITLE.PREREQUISITE}
              description={prerequisite}
              isOpen={DETAIL_TITLE.PREREQUISITE === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {outcome && outcome.length > 0 ? (
            <Collapsible
              title={DETAIL_TITLE.OUTCOME}
              description={outcome}
              isOpen={DETAIL_TITLE.OUTCOME === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {outline && outline.text && outline.text.length > 0 ? (
            <Collapsible
              title={DETAIL_TITLE.OUTLINE}
              description={outline.text}
              isOpen={DETAIL_TITLE.OUTLINE === currentSelect}
              changeModal={handleCollaps}
            />
          ) : null}

          {testimonials && testimonials.length > 0 ? (
            <div className="">
              <BundleTestimonialSection testimonials={testimonials} />
            </div>
          ) : null}

          {faqItem.length > 0 && (
            <>
              <div
                className="d-flex align-items-center justify-content-between flex-row w-100 pointer course-description__faq"
                onClick={() => setOpenFaqItem((prev) => !prev)}
              >
                <SubHeading variety={3} className="frequently-questions-section-title">
                  Frequently Asked Questions
                </SubHeading>
                <img src={openFaqItem ? ICONS.chevronUp : ICONS.chevronDown} alt="chevronDown" />
              </div>
              <div className="course-description__faq-items">
                {faqItem.length > 0 &&
                  openFaqItem &&
                  faqItem.map((row, index) => (
                    <Accordion
                      key={row.index}
                      question={row.question}
                      answer={row.answer}
                      isOpen={row.isOpen}
                      onClick={() => {
                        setFaqItem((prev) =>
                          prev.map((item, idx) => ({
                            ...item,
                            isOpen: idx === index ? !item.isOpen : false,
                          }))
                        );
                      }}
                    />
                  ))}
              </div>
            </>
          )}
        </div>
        <div className="course-description__checkout">
          <div className="card-floating description-checkout">
            <div className="image-container d-none d-lg-block">
              <img src={image ? image.path : '/dummy-img-video.png'} alt="course image" className="image"></img>
            </div>
            {Array.isArray(files) && files.length > 0 && (
              <div className={'mt-4 desktop-preview-button'}>
                <PreviewDocument buttonLabel={'Preview'} documentLinks={files.map((file) => file.path)} />
              </div>
            )}
            <div className="d-none d-lg-block">
              <CourseData
                isBundle={true}
                discount={discount}
                courseOrganizer={provider?.name}
                price={price}
                plannedCoursesCount={plannedCoursesCount}
                plannedCoursesProductsCount={plannedCoursesProductsCount}
                plannedProductsCount={plannedProductsCount}
                newCoursesProductsReleasedMonthly={newCoursesProductsReleasedMonthly}
                newCoursesReleasedMonthly={newCoursesReleasedMonthly}
                newProductsReleasedMonthly={newProductsReleasedMonthly}
                bundleType={bundleType}
                productType={productType}
                totalPages={totalPages}
                courseDuration={timeInvested}
                timeEstimation={timeEstimation}
                language={language || ''}
                center={center}
                publishDate={bundleReleaseDate}
                referenceNumber={referenceNumber}
                certificateAwarded={isHasCertification}
                version={version}
                formatType={type}
                facilitator={facilitator?.name}
                status={status ? 'Published' : null}
                totalCourseBundling={getTotalCoursesInBundles()}
              />
            </div>
            <Button
              type={isAddedToCart ? BUTTON_TYPE.addedToCart : BUTTON_TYPE.primary}
              onClick={() => handleAddToCart(bundleDetail)}
            >
              {isAddedToCart ? 'Remove from cart' : 'Add to cart'}
            </Button>
          </div>
        </div>
      </div>
      <div className="course-description__recomended">
        <SubHeading variety={isMobile ? 2 : 3} className="title-2">
          Recommended Bundles
        </SubHeading>
        <div className="course-container-not-bundling grid-display">
          {bundleRecommendation &&
            bundleRecommendation
              .filter((item) => item.slug !== slug)
              .slice(0, recommendedExpand ? bundleRecommendation.length : 3)
              .map((data) => {
                return (
                  <Card
                    key={data._id}
                    activeTabItemName={'bundles'}
                    isProduct={false}
                    setIsLoading={() => {}}
                    isLoading={false}
                    itemType={'bundles'}
                    item={data}
                  />
                );
              })}
        </div>
        {bundleRecommendation?.length - 2 > 0 && (
          <SubHeading variety={4} onClick={() => setRecommendedExpand((prev) => !prev)} className="show-more">
            {recommendedExpand ? 'Show Less' : 'Show More'}
          </SubHeading>
        )}
      </div>

      <div id="back-button-container">
        <Button
          fontType={FONT_TYPES.textBody}
          variety={isMobile ? 3 : 5}
          type={BUTTON_TYPE.secondary}
          onClick={() => router.back()}
        >
          Back
        </Button>
      </div>

      {openModalCheckout ? (
        <ModalCheckout
          setOpen={setOpenModalCheckout}
          open={openModalCheckout}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      ) : null}
    </div>
  );
};

export default BundleDescription;
